import { Button } from 'primereact/button';
import { Inplace, InplaceContent, InplaceDisplay } from 'primereact/inplace';
import { InputText, InputTextProps } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import ErrorMessage from '../../error-message';
import styles from './TextInplaceEdit.module.css';

export type TextInplaceEditProps<T extends FieldValues> = Omit<InputTextProps, 'name'> & {
  name: string;
  textSize?: string;
} & UseControllerProps<T>;

const TextInplaceEdit = <T extends FieldValues>({
  name,
  control,
  textSize = 'text-2xl',
  ...props
}: TextInplaceEditProps<T>) => {
  const buttonGroupRef = useRef<HTMLDivElement>(null);
  const [buttonGroupWidth, setButtonGroupWidth] = useState(0);

  const [active, setActive] = useState<boolean>(false);

  const {
    field: { ref, onChange, ...inputProps },
    fieldState: { invalid, error },
  } = useController({
    name,
    control,
  });

  // fallback to old value if cancel button is clicked
  const [currentValue, setCurrentValue] = useState(props.value);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event);
  };

  // Once the buttons are rendered, calculate their width and give that amount of right-padding to the input field
  // So that the buttons don't overlap the text in the input field
  useEffect(() => {
    if (buttonGroupRef.current) {
      setButtonGroupWidth(buttonGroupRef.current.offsetWidth);
    }
  }, [buttonGroupRef, active]);

  return (
    <div>
      <div className='m-0 p-0 font-semibold flex flex-row align-items-center display-contents cursor-pointer gap-2'>
        <Inplace
          active={active}
          onToggle={(e) => {
            setActive(e.value);
          }}
          onKeyDown={(e) => {
            if (!active || (e.key !== 'Enter' && e.key !== 'Escape')) {
              return;
            }

            if (e.key === 'Enter') {
              onChange(inputProps.value);
              setCurrentValue(inputProps.value);
            } else if (e.key === 'Escape') {
              onChange(currentValue);
            }

            setActive(false);
          }}
        >
          {/* The component to show when input field is closed */}
          <InplaceDisplay>
            <div
              className={classNames(
                'flex w-full font-bold line-height-2 border-1 border-m border-round-md border-transparent py-2 pl-2 hover:bg-dark-gray',
                textSize,
                styles['inplace-edit-display-container'],
              )}
            >
              {inputProps.value || props.placeholder}
            </div>
          </InplaceDisplay>
          {/* The component to show when input field is opened */}
          <InplaceContent>
            <div className={classNames('flex w-full align-items-center gap-2 relative')}>
              <InputText
                id={name}
                onChange={handleOnChange}
                autoFocus
                {...props}
                className={classNames('w-full pl-2 py-2 line-height-2', textSize, {
                  'p-invalid': error,
                })}
                style={{ paddingRight: `${buttonGroupWidth}px` }} // Dynamically set the right padding, based on the buttons' width
              />
              <div
                className={classNames(
                  'flex gap-2 absolute right-0 justify-content-center align-items-center h-full px-2',
                )}
                style={{ height: 'auto' }}
                ref={buttonGroupRef}
              >
                <Button
                  icon='pi pi-check'
                  className='p-1 w-auto aspect-square'
                  onClick={() => {
                    onChange(inputProps.value);
                    setCurrentValue(inputProps.value);
                    setActive(false);
                  }}
                />
                <Button
                  icon='pi pi-times'
                  className='bg-gray-600 p-1 w-auto aspect-square'
                  onClick={() => {
                    onChange(currentValue);
                    setActive(false);
                  }}
                />
              </div>
            </div>
          </InplaceContent>
        </Inplace>

        {/* Hidden input to enable focus & auto-scroll on error (wrapped in div due to width problems)*/}
        <div>
          <input readOnly className='opacity-0 p-0 m-0 w-0 h-0 absolute' ref={ref} />
        </div>
      </div>
      {invalid && (
        <div className='py-2'>
          <ErrorMessage message={error?.message || ''} />
        </div>
      )}
    </div>
  );
};

export default TextInplaceEdit;
